import { useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import {
  api,
  type EngagementSummary,
  type ReleasableItem,
  type ReleaseBoard,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { PreviewPanel } from "./PreviewPanel";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Button,
  Empty,
  Failed,
  Label,
  Mark,
  SkeletonRows,
} from "../components/primitives";
import { classificationLabel, day } from "../lib/format";
import { prefersReducedMotion, release as releaseMotion } from "../lib/motion";

/**
 * Release — the only screen in the product with an authored moment.
 *
 * `DESIGN.md` has specified it under Motion since the first build and listed
 * it under known gaps ever since: *"the publication moment described under
 * Motion is specified and not yet built: it needs the release surface, which
 * is the next screen."* This is that screen.
 *
 * **Why it is a screen and not a button.** Publishing has worked since
 * Slice 3, one item at a time, from whichever screen the item happened to
 * live on. What has never existed anywhere in the product is the question a
 * Lead actually asks before a client call — *what have we given them?* — and
 * the answer was reconstructed from memory every time.
 *
 * **The two columns are the point.** Left is ours, right is theirs, and the
 * only thing that moves an item between them is a deliberate act with a name
 * and a date on it. Nothing here is a toggle; a toggle implies symmetry, and
 * these two directions are not symmetric. Releasing is irreversible in
 * effect — a client who has read something has read it — and withdrawing
 * says so rather than pretending to undo it.
 *
 * **Two gates, and the screen says which one is shut.** A client sees an
 * item only if it is published AND classified at or below Client Visible.
 * `app.publish` refuses the second case outright, so they cannot come apart
 * by publishing — but reclassifying a published item upward is an ordinary
 * update, and then every other screen still says "released" while the client
 * sees nothing. Those rows are marked, in the right-hand column, as released
 * and not visible.
 *
 * Per DESIGN.md's prohibitions, publication is the one action that earns a
 * confirm, because it is irreversible in effect. It is the only one here.
 */

export function Release() {
  const { id } = useParams<{ id: string }>();
  const engagementId = id ?? "";

  const engagements = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );
  const board = useResource<ReleaseBoard>(
    () => api.get<ReleaseBoard>(`/api/engagements/${engagementId}/release`),
    [engagementId],
  );

  const engagement =
    engagements.resource.state === "ready"
      ? engagements.resource.data.find((e) => e.id === engagementId)
      : undefined;

  // The item currently crossing, so one row animates and the rest do not.
  // Motion reports a change; a list that re-animates wholesale on every
  // reload is decoration.
  const [crossing, setCrossing] = useState<string | null>(null);

  return (
    <AppShell
      engagement={
        engagement && {
          reference: engagement.reference,
          name: engagement.name,
          organisation: engagement.organisation,
        }
      }
    >
      <ScreenHead title="Release" />

      {board.resource.state === "loading" && <SkeletonRows rows={5} />}
      {board.resource.state === "failed" && (
        <Failed
          message={board.resource.message}
          correlationId={board.resource.correlationId}
        />
      )}

      {board.resource.state === "ready" && (
        <>
          <p className="max-w-[62ch] text-base text-secondary">
            Review what the client can receive, release items when they are
            ready, and see who released each one. Withdrawing an item removes
            access but keeps the release history.
          </p>

          <Preconditions board={board.resource.data} />

          <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:gap-12">
            <Column
              heading="Not released"
              note="The client cannot see these items."
              empty="Everything ready for the client has been released"
              items={board.resource.data.held}
              side="held"
              mayRelease={board.resource.data.mayRelease}
              engagementId={engagementId}
              crossing={crossing}
              onCross={setCrossing}
              onChanged={board.reload}
            />

            <Column
              heading="Released to the client"
              note="The client can see these items. Each one shows who released it and when."
              empty="Nothing has been released yet"
              items={board.resource.data.released}
              side="released"
              mayRelease={board.resource.data.mayRelease}
              engagementId={engagementId}
              crossing={crossing}
              onCross={setCrossing}
              onChanged={board.reload}
            />
          </div>

          <PreviewPanel
            engagementId={engagementId}
            mayRelease={board.resource.data.mayRelease}
          />

          {!board.resource.data.mayRelease && (
            <p className="mt-12 max-w-[62ch] border-l-2 border-line pl-4 text-sm text-secondary">
              Only a Lead or Partner can release items. You can still see
              what is available to the client.
            </p>
          )}
        </>
      )}
    </AppShell>
  );
}

/**
 * The two things that make every release on this screen a no-op, stated once
 * at the top instead of being discovered item by item.
 *
 * Both are real states somebody has reached. An engagement classified above
 * Client Visible cannot be seen by a client however much is published to it
 * — that one cost a live client a workspace they were told was ready. A
 * portal that was never opened is the same silence from the other end.
 */
function Preconditions({ board }: { board: ReleaseBoard }) {
  if (board.portalOpen && !board.engagementAboveClient) return null;

  return (
    <div className="mt-6 max-w-[62ch] border-l-2 border-danger pl-4">
      {board.engagementAboveClient && (
        <p className="text-base text-ink">
          This engagement is not marked Client visible. Change that in Setup
          before releasing anything.
        </p>
      )}
      {!board.portalOpen && (
        <p className="mt-2 text-base text-ink">
          The client portal is closed. Open it in Setup before the client can
          view released items.
        </p>
      )}
    </div>
  );
}

function Column({
  heading,
  note,
  empty,
  items,
  side,
  mayRelease,
  engagementId,
  crossing,
  onCross,
  onChanged,
}: {
  heading: string;
  note: string;
  empty: string;
  items: ReleasableItem[];
  side: "held" | "released";
  mayRelease: boolean;
  engagementId: string;
  crossing: string | null;
  onCross: (id: string | null) => void;
  onChanged: () => void;
}) {
  return (
    <section>
      <div className="border-b border-line-strong pb-3">
        <Label as="h2">{heading}</Label>
        <p className="mt-1 max-w-[46ch] text-sm text-secondary">{note}</p>
      </div>

      {items.length === 0 ? (
        <Empty title={empty} />
      ) : (
        <ul>
          <AnimatePresence initial={false}>
            {items.map((item) => (
              <Row
                key={`${item.type}-${item.id}`}
                item={item}
                side={side}
                mayRelease={mayRelease}
                engagementId={engagementId}
                crossing={crossing === `${item.type}-${item.id}`}
                onCross={onCross}
                onChanged={onChanged}
              />
            ))}
          </AnimatePresence>
        </ul>
      )}
    </section>
  );
}

/**
 * One item, on one side.
 *
 * The crossing is the authored moment: the row carries out towards the
 * other column, the list closes behind it, and the reloaded board draws it
 * in on the far side. `prefers-reduced-motion` removes the transform
 * entirely — the state change stays completely legible as an instant swap,
 * which is the rule the whole motion vocabulary is written under.
 */
function Row({
  item,
  side,
  mayRelease,
  engagementId,
  crossing,
  onCross,
  onChanged,
}: {
  item: ReleasableItem;
  side: "held" | "released";
  mayRelease: boolean;
  engagementId: string;
  crossing: boolean;
  onCross: (id: string | null) => void;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const reduced = prefersReducedMotion();
  const key = `${item.type}-${item.id}`;

  const releaseIt = useAction(async () => {
    onCross(key);
    // `share` rather than `publish`: one act. Reclassifying and publishing
    // separately is how you get an item marked Client Visible that was
    // never released, or a publication the classification keeps invisible —
    // which is the exact state the right-hand column has to mark.
    await api.post(`/api/engagements/${engagementId}/share`, {
      type: item.type,
      objectId: item.id,
    });
    setConfirming(false);
    onChanged();
    onCross(null);
  });

  const withdrawIt = useAction(async () => {
    onCross(key);
    await api.post("/api/withdrawals", { type: item.type, objectId: item.id });
    onChanged();
    onCross(null);
  });

  const refusal = releaseIt.refusal ?? withdrawIt.refusal;

  return (
    <motion.li
      layout={!reduced}
      exit={
        reduced
          ? { opacity: 0 }
          : { opacity: 0, x: side === "held" ? 40 : -40, transition: releaseMotion }
      }
      animate={crossing && !reduced ? { opacity: 0.4 } : { opacity: 1 }}
      transition={releaseMotion}
      className="border-b border-line py-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-2">
        <div className="min-w-0">
          <p className="text-base text-ink">{item.name}</p>
          <p className="mt-1 text-sm text-secondary">
            {KIND[item.type] ?? item.type} ·{" "}
            {classificationLabel(item.classification)}
          </p>
        </div>

        {side === "released" && (
          <div className="text-right text-sm text-secondary">
            {item.publishedByName && item.publishedAt && (
              <p>
                {item.publishedByName} · {day(item.publishedAt)}
              </p>
            )}
            {!item.visible && (
              // Published and invisible. The one state no other screen in
              // the product can tell you about.
              <p className="mt-1">
                <Mark tone="danger">not visible</Mark>
              </p>
            )}
          </div>
        )}

        {side === "held" && item.revokedAt && (
          <p className="text-right text-sm text-secondary">
            withdrawn {day(item.revokedAt)}
            {item.revokedByName && ` by ${item.revokedByName}`}
          </p>
        )}
      </div>

      {mayRelease && (
        <div className="mt-3">
          {side === "held" ? (
            confirming ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-ink">
                  Release this item to the client? They will be notified and
                  may view it immediately.
                </p>
                <Button
                  tone="primary"
                  loading={releaseIt.pending}
                  onClick={() => void releaseIt.invoke()}
                >
                  Release it
                </Button>
                <Button tone="quiet" onClick={() => setConfirming(false)}>
                  Not yet
                </Button>
              </div>
            ) : (
              <Button tone="secondary" onClick={() => setConfirming(true)}>
                Release to the client
              </Button>
            )
          ) : (
            // No confirm on the way back. Withdrawal is the cautious
            // direction and making it the slower one would be exactly
            // backwards.
            <Button
              tone="quiet"
              loading={withdrawIt.pending}
              onClick={() => void withdrawIt.invoke()}
            >
              Withdraw
            </Button>
          )}
        </div>
      )}

      {refusal && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {refusal.message}
        </p>
      )}
    </motion.li>
  );
}

const KIND: Record<string, string> = {
  deliverable: "Deliverable",
  document: "Document",
  milestone: "Milestone",
  workstream: "Workstream",
  status_update: "Update",
  client_action: "Request",
};
